import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { vehiclePickerApi } from '../../services/vehiclePickerApi';
import { catalogNodeService } from '../../services/apiService';

const FUEL_TYPES = ['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid', 'Dual Fuel'];
const TRANSMISSIONS = [
  'Manual 5-speed',
  'Manual 6-speed',
  'AMT',
  'CVT',
  'Torque Converter AT',
  'DCT',
  'iMT',
];

const emptyPath = () => ({
  make: '',
  model: '',
  generation: '',
  variant: '',
  fuelType: '',
  transmission: '',
  engineCode: '',
});

/**
 * 7-layer catalog fitment picker for seller listing wizard.
 * Profile (A–E) is inferred server-side from partCategory — sellers never see layer numbers.
 */
export default function CatalogFitmentSelector({
  value,
  partCategory = '',
  subSystem = '',
  onChange,
  onResolved,
  onGenerationMeta,
}) {
  const path = value || emptyPath();

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selectedMakeId, setSelectedMakeId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedGenId, setSelectedGenId] = useState('');
  const [resolvePreview, setResolvePreview] = useState(null);
  const [resolveError, setResolveError] = useState('');

  useEffect(() => {
    vehiclePickerApi.listMakes().then(setMakes).catch(() => setMakes([]));
  }, []);

  const updatePath = useCallback(
    (patch) => {
      onChange?.({ ...path, ...patch });
    },
    [onChange, path]
  );

  useEffect(() => {
    if (!selectedMakeId) {
      setModels([]);
      return;
    }
    vehiclePickerApi.listModels(selectedMakeId).then(setModels).catch(() => setModels([]));
  }, [selectedMakeId]);

  useEffect(() => {
    if (!selectedModelId) {
      setGenerations([]);
      return;
    }
    vehiclePickerApi.listGenerations(selectedModelId).then(setGenerations).catch(() => setGenerations([]));
  }, [selectedModelId]);

  useEffect(() => {
    if (!selectedGenId) {
      setVariants([]);
      return;
    }
    vehiclePickerApi.listVariants(selectedGenId).then(setVariants).catch(() => setVariants([]));
  }, [selectedGenId]);

  const canResolve = useMemo(
    () => Boolean(path.make && path.model && path.generation && partCategory?.trim()),
    [path.make, path.model, path.generation, partCategory]
  );

  useEffect(() => {
    if (!canResolve) {
      setResolvePreview(null);
      setResolveError('');
      onResolved?.(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setResolveError('');
        const data = await catalogNodeService.resolve({
          ...path,
          partCategory: partCategory.trim(),
          subSystem: subSystem || undefined,
        });
        const resolved = data?.data || data;
        setResolvePreview(resolved);
        onResolved?.(resolved);
      } catch (err) {
        setResolvePreview(null);
        setResolveError(err.message || 'Could not resolve catalog node');
        onResolved?.(null);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [canResolve, path, partCategory, subSystem, onResolved]);

  const showTransmission = resolvePreview?.resolvedPath?.transmission !== 'ALL';
  const showEngineCode = resolvePreview?.resolvedPath?.engineCode !== 'ALL';
  const showVariant = resolvePreview?.resolvedPath?.variant !== 'ALL' || !resolvePreview;
  const showFuel = resolvePreview?.resolvedPath?.fuelType !== 'ALL' || !resolvePreview;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Make *</label>
          <select
            value={selectedMakeId}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedMakeId(id);
              setSelectedModelId('');
              setSelectedGenId('');
              const make = makes.find((m) => String(m.id || m._id) === id);
              updatePath({
                make: make?.name || make?.label || '',
                model: '',
                generation: '',
                variant: '',
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Select make</option>
            {makes.map((m) => (
              <option key={m.id || m._id} value={m.id || m._id}>
                {m.label || m.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Model *</label>
          <select
            value={selectedModelId}
            disabled={!selectedMakeId}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedModelId(id);
              setSelectedGenId('');
              const model = models.find((m) => String(m.id || m._id) === id);
              updatePath({
                model: model?.name || model?.label || '',
                generation: '',
                variant: '',
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
          >
            <option value="">Select model</option>
            {models.map((m) => (
              <option key={m.id || m._id} value={m.id || m._id}>
                {m.label || m.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Generation *</label>
          <select
            value={selectedGenId}
            disabled={!selectedModelId}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedGenId(id);
              const gen = generations.find((g) => String(g.id || g._id) === id);
              onGenerationMeta?.({
                yearStart: gen?.yearStart ?? null,
                yearEnd: gen?.yearEnd ?? null,
              });
              updatePath({
                generation: gen?.label || gen?.generation || '',
                variant: '',
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
          >
            <option value="">Select generation</option>
            {generations.map((g) => (
              <option key={g.id || g._id} value={g.id || g._id}>
                {g.label || g.generation}
              </option>
            ))}
          </select>
        </div>

        {showVariant && (
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Variant</label>
            <select
              disabled={!selectedGenId}
              value={path.variant || ''}
              onChange={(e) => {
                const v = variants.find((x) => x.variant === e.target.value);
                updatePath({
                  variant: e.target.value,
                  fuelType: v?.fuelType || path.fuelType,
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
            >
              <option value="">Select variant (if applicable)</option>
              {variants.map((v) => (
                <option key={v.id || v._id} value={v.variant}>
                  {v.label || v.variant}
                </option>
              ))}
            </select>
          </div>
        )}

        {showFuel && (
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Fuel type</label>
            <select
              value={path.fuelType || ''}
              onChange={(e) => updatePath({ fuelType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select fuel (if applicable)</option>
              {FUEL_TYPES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
        )}

        {showTransmission && (
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Transmission *</label>
            <select
              value={path.transmission || ''}
              onChange={(e) => updatePath({ transmission: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select transmission</option>
              {TRANSMISSIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        )}

        {showEngineCode && (
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Engine code *</label>
            <input
              type="text"
              value={path.engineCode || ''}
              onChange={(e) => updatePath({ engineCode: e.target.value })}
              placeholder="e.g. K12M, DDiS 1.3L"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        )}
      </div>

      {resolveError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {resolveError}
        </p>
      )}

      {resolvePreview?.catalogNodeId && (
        <div className="text-xs bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 space-y-1">
          <p className="font-semibold text-indigo-900">
            Catalog node ready · Profile {resolvePreview.profile}
            {resolvePreview.subSystem ? ` · ${resolvePreview.subSystem}` : ''}
          </p>
          <p className="text-indigo-700 break-all font-mono text-[10px]">
            {resolvePreview.catalogNodeId}
          </p>
        </div>
      )}
    </div>
  );
}

export { emptyPath as emptyCatalogVehiclePath };
